import { Polygon } from "geojson";
import { useEffect, useMemo } from "react";

import { getDistanceInM } from "@nens/shared-utils";

import drawImage from "./flow-utils/drawImage";
import { WindGL } from "./flow-utils/main";
import { TwoDWaterflowProps } from "./two-d-waterflow";
import { BasicInfo, Bounds, WindData, WindowWind } from "./types";

interface UsePrepareHooksProps extends TwoDWaterflowProps {
  getCanvasses: () => {
    canvas1: HTMLCanvasElement | null;
    canvas2: HTMLCanvasElement | null;
  };
}

// Here is where prepare the 2D flow. Get the basicInfo like the bounds, setup the canvasses and draw a new image if new velocities come in
export const usePrepareHooks = ({
  features,
  twoDFlowData,
  config,
  getCanvasses,
}: UsePrepareHooksProps) => {
  // Calculate basic info
  const basicInfo: BasicInfo = useMemo(() => {
    let bounds: Bounds = [10000, -10000, -10000, 10000];

    features.forEach((feature) => {
      const coordinates = (feature.geometry as Polygon).coordinates[0];
      const xCoordinates = coordinates.flatMap((coordinate) => coordinate[0]);
      const yCoordinates = coordinates.flatMap((coordinate) => coordinate[1]);
      bounds = [
        Math.min(...xCoordinates, bounds[0]),
        Math.max(...yCoordinates, bounds[1]),
        Math.max(...xCoordinates, bounds[2]),
        Math.min(...yCoordinates, bounds[3]),
      ];
    });

    // Calculate width and height
    const xDistanceM = getDistanceInM([bounds[0], 0], [bounds[2], 0]);
    const yDistanceM = getDistanceInM([0, bounds[1]], [0, bounds[3]]);
    const percentage = 2500 / Math.max(xDistanceM, yDistanceM);
    const width = xDistanceM * percentage;
    const height = yDistanceM * percentage;

    return {
      bounds,
      width,
      height,
    };
  }, [features]);

  // Resize canvas to found basic info
  useEffect(() => {
    const { canvas1, canvas2 } = getCanvasses();
    if (canvas1 && canvas2 && basicInfo) {
      const { width, height } = basicInfo;
      const setupCanvasGl = (canvas: HTMLCanvasElement, windKey: "wind1" | "wind2") => {
        if (canvas.classList.contains("offscreenControl")) return;
        canvas.classList.add("offscreenControl");

        canvas.width = width;
        canvas.height = height;

        const offscreen = canvas.transferControlToOffscreen();
        const gl = offscreen.getContext("webgl2", {
          antialiasing: false,
        }) as WebGL2RenderingContext;

        (window as WindowWind)[windKey] = new WindGL(gl);

        const wind = (window as WindowWind)[windKey];
        wind && wind.resize();
      };
      setupCanvasGl(canvas1, "wind1");
      setupCanvasGl(canvas2, "wind2");
    }
  }, [basicInfo]);

  const windData: WindData | null = useMemo(() => {
    if (!basicInfo) return null;
    const { bounds, width, height } = basicInfo;
    return (
      drawImage({
        features,
        width,
        height,
        bounds,
        twoDFlowData,
        minVelocity: config ? config.waterflowThreshold : 0,
      }) || null
    );
  }, [basicInfo, features, twoDFlowData, config]);

  return { windData, basicInfo };
};
